import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Star, Zap, FileSpreadsheet, Upload, Download, ChevronDown, Filter } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { GlassCard, GradButton, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { AraCubugu, Th, BosDurum, DetayIkon } from "@/components/ortak/Liste";
import { useSayfa, useSecim } from "@/components/ortak/KayitAraclari";
import { TekrarliMaaslar } from "@/components/cari/TekrarliMaaslar";
import { KolaybiEkstreDialog } from "@/components/cari/KolaybiEkstre";
import { useAuth } from "@/context/AuthContext";
import { can, CARI_TIP } from "@/lib/perms";
import { SEKMELER, PERSONEL_TIP, pazaryeriMi, etiketleri } from "@/lib/cari";
import api, { errMsg } from "@/lib/api";
import { eslesir } from "@/lib/ara";

export const RiskBar = ({ risk, testid }) => {
  if (!risk?.limit) return <span className="text-xs text-amber-400/80" data-testid={testid}>Limit tanımsız · {fmtMoney(risk?.kullanim)}</span>;
  const pct = Math.min(100, risk.yuzde);
  const renk = risk.yuzde >= 100 ? "bg-red-500" : risk.yuzde >= 80 ? "bg-amber-400" : "grad-bg";
  return (
    <div className="min-w-[160px]" data-testid={testid}>
      <div className="flex justify-between text-[11px]"><span className="font-mono">{fmtMoney(risk.kullanim)}</span><span className="text-muted-foreground">/ {fmtMoney(risk.limit)}</span></div>
      <div className="h-1.5 rounded-full bg-white/5 mt-1 overflow-hidden"><div className={`h-full rounded-full ${renk}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

// Bizim gözümüzden: bakiye > 0 alacaklıyız (yeşil), < 0 borçluyuz (kırmızı)
export const BakiyeHucre = ({ v, pb, testid, bakiyeler }) => (
  <div className="text-right" data-testid={testid}>
    <div className={`font-mono text-xs ${v > 0.004 ? "text-emerald-400" : v < -0.004 ? "text-red-400" : ""}`}>{fmtMoney(v, pb)}</div>
    {Math.abs(v) > 0.004 && <div className={`text-[10px] ${v > 0 ? "text-emerald-400" : "text-red-400"}`}>{v > 0 ? "Alacaklıyız" : "Borçluyuz"}</div>}
    {bakiyeler && Object.keys(bakiyeler).length > 1 && <div className="text-[10px] text-muted-foreground whitespace-nowrap">{Object.entries(bakiyeler).map(([k, x]) => fmtMoney(x, k)).join(" · ")}</div>}
  </div>
);

export const CariSekmeler = ({ aktif }) => {
  const nav = useNavigate();
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-white/10 mb-4 -mt-2" data-testid="cari-sekmeler">
      {SEKMELER.map((s) => <button key={s.k} onClick={() => nav(s.k === "genel" ? "/cariler" : `/cariler?sekme=${s.k}`)} data-testid={`cari-sekme-${s.k}`}
        className={`px-3.5 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${aktif === s.k ? "border-[var(--g1)] text-[var(--g1)] font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{s.ad}</button>)}
    </div>
  );
};

const ALAN = { tumu: "Tümü", unvan: "Ünvan", kod: "Cari Kodu", telefon: "Telefon", vergi_no: "VKN/TCKN", etiket: "Etiket", il: "İl" };
const FILTRE_BOS = { tip: "", durum: "", aktif: "aktif" };
const zamanYaz = (iso) => {
  const dk = Math.round((Date.now() - new Date(iso)) / 60000);
  if (dk < 60) return `${Math.max(dk, 1)} dk önce`;
  if (dk < 1440) return `${Math.round(dk / 60)} saat önce`;
  if (dk < 10080) return `${Math.round(dk / 1440)} gün önce`;
  return new Date(iso).toLocaleDateString("tr-TR");
};


const Filtre = ({ f, setF, personel }) => (
  <Popover>
    <PopoverTrigger asChild><button data-testid="cari-filtre" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-xs font-semibold border border-[var(--g1)]/50 text-[var(--g1)] hover:bg-white/5"><Filter className="w-4 h-4" />Filtrele{(f.tip || f.durum || f.aktif !== (personel ? "" : "aktif")) && " •"}</button></PopoverTrigger>
    <PopoverContent className="glass w-72 space-y-3" align="end">
      <div className="space-y-1"><div className="text-xs text-muted-foreground">{personel ? "Personel tipi" : "Cari tipi"}</div>
        <SelectBox testid="cari-filtre-tip" value={f.tip} onChange={(v) => setF({ ...f, tip: v })} options={[{ value: "", label: "Tümü" }, ...Object.entries(personel ? PERSONEL_TIP : CARI_TIP).filter(([k]) => k !== "personel").map(([value, label]) => ({ value, label }))]} /></div>
      <div className="space-y-1"><div className="text-xs text-muted-foreground">Bakiye</div>
        <SelectBox testid="cari-filtre-durum" value={f.durum} onChange={(v) => setF({ ...f, durum: v })} options={[{ value: "", label: "Tümü" }, { value: "borclu", label: "Alacaklı olduklarımız" }, { value: "alacakli", label: "Borçlu olduklarımız" }, { value: "sifir", label: "Bakiyesiz" }]} /></div>
      <div className="space-y-1"><div className="text-xs text-muted-foreground">Durum</div>
        <SelectBox testid="cari-filtre-aktif" value={f.aktif} onChange={(v) => setF({ ...f, aktif: v })} options={[{ value: "aktif", label: personel ? "Çalışanlar" : "Aktif" }, { value: "pasif", label: personel ? "Ayrılanlar" : "Pasif" }, { value: "", label: "Tümü" }]} /></div>
      <button onClick={() => setF(personel ? { ...FILTRE_BOS, aktif: "" } : FILTRE_BOS)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="cari-filtre-temizle">Filtreyi temizle</button>
    </PopoverContent>
  </Popover>
);

const Toplu = ({ secili, bitti }) => {
  const [etiket, setEtiket] = useState("");
  const yap = async (islem, deger = "") => {
    try { const { data } = await api.post("/cariler/toplu", { idler: [...secili], islem, deger }); toast.success(`${data.guncellenen} cari güncellendi`); bitti(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button disabled={!secili.size} data-testid="cari-toplu" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs border border-white/15 disabled:opacity-40 hover:bg-white/5"><Zap className="w-3.5 h-3.5" />Toplu İşlemler{secili.size ? ` (${secili.size})` : ""}</button></DropdownMenuTrigger>
      <DropdownMenuContent className="glass w-64" align="start">
        <div className="p-2 flex gap-1.5" onKeyDown={(e) => e.stopPropagation()}><Input data-testid="cari-toplu-etiket" value={etiket} onChange={(e) => setEtiket(e.target.value)} placeholder="Etiket" className="h-8 text-xs bg-white/5" /></div>
        <DropdownMenuItem data-testid="cari-toplu-etiket-ekle" onClick={() => yap("etiket_ekle", etiket)}>Etiketi ekle</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-etiket-cikar" onClick={() => yap("etiket_cikar", etiket)}>Etiketi çıkar</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-pasif" onClick={() => yap("pasif")}>Pasife al</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-aktif" onClick={() => yap("aktif")}>Aktife al</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-potansiyel" onClick={() => yap("kategori", "potansiyel")}>Potansiyel müşteriye taşı</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-genel" onClick={() => yap("kategori", "genel")}>Genel carilere taşı</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-toplu-isortagi" onClick={() => yap("kategori", "is_ortagi")}>İş ortaklarına taşı</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ExcelMenu = ({ kategori, sekme, admin, yenile }) => {
  const nav = useNavigate();
  const [kb, setKb] = useState(false);
  const indir = async () => {
    try {
      const r = await api.get("/cariler-disa-aktar", { params: { kategori, sekme }, responseType: "blob" });
      const ad = decodeURIComponent((r.headers["content-disposition"] || "").split("''")[1] || "Cariler.xlsx");
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = ad; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
    {kb && <KolaybiEkstreDialog kapat={() => setKb(false)} bitti={(c) => { setKb(false); yenile(); nav(`/cariler/${c.id}`); }} />}
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button data-testid="cari-excel" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-xs font-semibold border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><FileSpreadsheet className="w-4 h-4" />Excel İşlemleri<ChevronDown className="w-3.5 h-3.5" /></button></DropdownMenuTrigger>
      <DropdownMenuContent className="glass" align="end">
        {admin && <DropdownMenuItem data-testid="cari-excel-ice" onClick={() => nav("/toplu-aktarim")}><Upload className="w-4 h-4 mr-2" />İçe aktar</DropdownMenuItem>}
        <DropdownMenuItem data-testid="cari-excel-disa" onClick={indir}><Download className="w-4 h-4 mr-2" />Dışa aktar</DropdownMenuItem>
        <DropdownMenuItem data-testid="cari-excel-kolaybi" onClick={() => setKb(true)}><Upload className="w-4 h-4 mr-2" />KolayBi ekstresi yükle (PDF)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
};

const Satir = ({ c, s, personel, secim, favori, nav }) => (
  <tr className={`border-t border-white/5 hover:bg-white/[0.03] cursor-pointer ${c.aktif === false || c.isten_cikis ? "opacity-45" : ""}`} data-testid={`cari-satir-${c.kod}`} onClick={() => nav(`/cariler/${c.id}`)}>
    <td className="p-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={secim.sec.has(c.id)} onChange={() => secim.degis(c.id)} data-testid={`cari-sec-${c.kod}`} /></td>
    <td className="p-2.5"><DetayIkon /></td>
    <td className="p-2.5" onClick={(e) => { e.stopPropagation(); favori(c); }}><Star data-testid={`cari-favori-${c.kod}`} className={`w-4 h-4 ${c.favori ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} /></td>
    <td className="p-2.5 font-semibold text-xs max-w-[300px] truncate">{c.unvan.toLocaleUpperCase("tr")}</td>
    <td className="p-2.5 font-mono text-xs">{c.kod}</td>
    <td className="p-2.5 text-xs">{personel ? PERSONEL_TIP[c.personel_tipi] : s === "potansiyel" ? "Potansiyel Müşteri" : CARI_TIP[c.tip]}</td>
    <td className="p-2.5 text-xs">{c.telefon}</td>
    <td className="p-2.5">{etiketleri(c).map((e) => <span key={e} className="inline-block mr-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-600/80 text-white">{e}</span>)}</td>
    <td className="p-2.5 text-xs font-mono">{c.vergi_no}</td>
    <td className="p-2.5"><BakiyeHucre v={c.risk?.bakiye || 0} bakiyeler={c.risk?.bakiyeler} testid={`cari-bakiye-${c.kod}`} /></td>
    {!personel && <td className="p-2.5 text-xs whitespace-nowrap" data-testid={`cari-portal-giris-${c.kod}`} title={c.portal_ekstre_goruldu ? `Ekstreyi son görüntüleme: ${zamanYaz(c.portal_ekstre_goruldu)}` : "Ekstre portalda hiç açılmadı"}>{c.portal_son_giris ? zamanYaz(c.portal_son_giris) : <span className="text-muted-foreground">—</span>}{c.portal_ekstre_goruldu && <div className="text-[10px] text-emerald-300">ekstre görüldü</div>}</td>}
    {personel && <><td className="p-2.5 text-xs">{c.ise_giris ? c.ise_giris.slice(0, 10).split("-").reverse().join(".") : ""}</td><td className="p-2.5 text-xs" data-testid={`cari-gorev-${c.kod}`}>{c.gorev || "—"}{c.departman ? <span className="text-muted-foreground"> · {c.departman}</span> : null}</td></>}
  </tr>
);

export default function Cariler() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const s = SEKMELER.some((x) => x.k === params.get("sekme")) ? params.get("sekme") : "genel";
  const sekme = SEKMELER.find((x) => x.k === s);
  const personel = s === "personel";
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [alan, setAlan] = useState("tumu");
  const [f, setF] = useState(FILTRE_BOS);
  const secim = useSecim();
  const yazabilir = can(user, "cari:write");

  const load = useCallback(async () => {
    if (!sekme.kategori) return;
    const { data } = await api.get("/cariler", { params: { kategori: sekme.kategori } });
    setList(data);
  }, [sekme.kategori]);
  useEffect(() => { setList([]); secim.temizle(); setF(personel ? { ...FILTRE_BOS, aktif: "" } : FILTRE_BOS); load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const favori = async (c) => {
    try { c.favori ? await api.delete(`/cariler/${c.id}/favori`) : await api.post(`/cariler/${c.id}/favori`); setList((l) => l.map((x) => (x.id === c.id ? { ...x, favori: !c.favori } : x))); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = useMemo(() => list.filter((c) => {
    if (s === "pazaryeri" ? !pazaryeriMi(c) : s === "genel" && pazaryeriMi(c)) return false;
    const pasif = personel ? !!c.isten_cikis || c.aktif === false : c.aktif === false;
    if ((f.aktif === "aktif" && pasif) || (f.aktif === "pasif" && !pasif)) return false;
    if (f.tip && (personel ? c.personel_tipi : c.tip) !== f.tip) return false;
    const b = c.risk?.bakiye || 0;
    if ((f.durum === "borclu" && b <= 0.004) || (f.durum === "alacakli" && b >= -0.004) || (f.durum === "sifir" && Math.abs(b) > 0.004)) return false;
    const et = etiketleri(c).join(" ");
    return alan === "tumu" ? eslesir(q, c.kod, c.unvan, et, c.il, c.ilce, c.kisa_ad, c.telefon, c.vergi_no) : eslesir(q, alan === "etiket" ? et : c[alan]);
  }).sort((a, b) => Number(!!b.favori) - Number(!!a.favori)), [list, q, alan, f, s, personel]);
  const { gorunen, Sayfalama } = useSayfa(rows, `${q}${alan}${s}${JSON.stringify(f)}`);
  const hepsiSecili = gorunen.length > 0 && gorunen.every((c) => secim.sec.has(c.id));

  return (
    <div data-testid="cariler-page">
      <CariSekmeler aktif={s} />
      {s === "maas" ? <TekrarliMaaslar /> : <>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h1 className="font-head text-xl font-semibold text-[var(--g1)]" data-testid="cari-baslik">{sekme.ad}</h1>
          <div className="ml-auto flex gap-2">
            <Filtre f={f} setF={setF} personel={personel} />
            {yazabilir && s !== "pazaryeri" && <GradButton data-testid="cari-ekle" onClick={() => nav(`/cariler/yeni?kategori=${sekme.kategori}`)}><Plus className="w-4 h-4" />{personel ? "Yeni Personel" : "Yeni Cari"}</GradButton>}
          </div>
        </div>
        <GlassCard className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            {yazabilir && <Toplu secili={secim.sec} bitti={() => { secim.temizle(); load(); }} />}
            <div className="flex-1"><AraCubugu testid="cari" alanlar={ALAN} alan={alan} setAlan={setAlan} q={q} setQ={setQ} sol={null} /></div>
            <div className="mb-3"><ExcelMenu kategori={sekme.kategori} sekme={s} admin={user.role === "admin"} yenile={load} /></div>
          </div>
          {rows.length === 0 ? <BosDurum testid="cari-bos" text={q.trim() ? "Aramanıza uygun cari bulunamadı." : "Veri yok"} /> : (
            <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="cari-tablo">
              <thead><tr><Th className="w-8"><input type="checkbox" checked={hepsiSecili} onChange={(e) => secim.hepsi(gorunen.map((c) => c.id), e.target.checked)} data-testid="cari-hepsini-sec" /></Th><Th className="w-8" /><Th className="w-8" />
                <Th>Ünvan</Th><Th>Cari Kodu</Th><Th>Cari Tipi</Th><Th>Telefon</Th><Th>Etiketler</Th><Th>VKN/TCKN</Th><Th right>Bakiye</Th>{!personel && <Th>Portal son giriş</Th>}{personel && <><Th>İşe Giriş Tarihi</Th><Th>Görev</Th></>}</tr></thead>
              <tbody>{gorunen.map((c) => <Satir key={c.id} c={c} s={s} personel={personel} secim={secim} favori={favori} nav={nav} />)}</tbody>
            </table></div>
          )}
          {rows.length > 0 && <Sayfalama />}
        </GlassCard>
      </>}
    </div>
  );
}
